import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { config } from "../lib/config";
import { COPY } from "../shared/copy";
import { safeRedirect } from "../shared/redirect";
import { InstallSteps, InstallWhy } from "../components/InstallSteps";
import { InstallMedia } from "../components/InstallMedia";
import { isIOS, isStandalone, isIOSOtherBrowser } from "../lib/platform";

// Supabase auth errors are written for developers. Translate the ones a real
// person can actually hit; anything unexpected gets a plain sentence plus a way
// forward, never a raw provider string.
function signInMessage(raw: string): string {
  const m = raw.toLowerCase();
  const wait = raw.match(/after (\d+) seconds?/i);
  if (wait) return `Give it ${wait[1]} seconds, then send it again.`;
  if (m.includes("rate limit") || m.includes("too many")) {
    return "That's a lot of tries in a row. Wait a minute, then try again.";
  }
  if (m.includes("invalid") && m.includes("email")) {
    return "That email doesn't look right. Check it and try again.";
  }
  if (m.includes("fetch") || m.includes("network") || m.includes("failed to fetch")) {
    return "Couldn't reach us. Check your connection and try again.";
  }
  return "That didn't go through. Try again in a moment.";
}

function codeMessage(raw: string): string {
  const m = raw.toLowerCase();
  if (m.includes("expired")) return "That code expired. Send yourself a new one.";
  if (m.includes("fetch") || m.includes("network")) {
    return "Couldn't reach us. Check your connection and try again.";
  }
  return "That code didn't work. Check the numbers and try again.";
}

// Passwordless sign-in. Supabase sends BOTH a magic link and an 8-digit code; we
// lead with the code because a link cannot sign you in inside an installed iOS
// web app — tapping it opens Safari, which has its own separate session, leaving
// the Home Screen app logged out. The code completes sign-in right here.
export function Login() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [phase, setPhase] = useState<"email" | "code">("email");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");
  const codeRef = useRef<HTMLInputElement>(null);

  // The page they were trying to reach before we asked them to sign in.
  const destination = safeRedirect((location.state as any)?.from);

  // Already signed in (e.g. came back to /login by accident) — get out of the way,
  // and still honor where they were headed.
  useEffect(() => {
    if (user) navigate(destination, { replace: true });
  }, [user, navigate, destination]);

  // Phones capitalize and autocorrect their way into an address that does not
  // exist. Normalizing here means the code request, the invite claim, and the
  // verify step all use the exact same string.
  const cleanEmail = email.trim().toLowerCase();
  const emailLooksReal = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail);

  async function send(resend = false) {
    setErr("");
    setNote("");
    setBusy(true);
    try {
      // If they came from an invite, tie that invite to this email server-side so
      // the join completes no matter which browser or app opens the sign-in.
      const pendingToken = localStorage.getItem("pending_invite");
      if (pendingToken) {
        try { await api("/api/invite-claim", { body: { token: pendingToken, email: cleanEmail } }); } catch { /* non-fatal */ }
      }
      const { error } = await supabase.auth.signInWithOtp({
        email: cleanEmail,
        options: { emailRedirectTo: `${config.appUrl}/` },
      });
      if (error) {
        setErr(signInMessage(error.message));
      } else {
        setPhase("code");
        // Silence here reads as a dead button, since the only proof lives in a
        // different app.
        if (resend) setNote("Sent. A new code is on the way.");
        setTimeout(() => codeRef.current?.focus(), 50);
      }
    } catch (e: any) {
      setErr(signInMessage(String(e?.message ?? e)));
    } finally {
      setBusy(false);
    }
  }

  async function verify() {
    setErr("");
    setNote("");
    setBusy(true);
    try {
      const { error } = await supabase.auth.verifyOtp({
        email: cleanEmail,
        token: code.trim(),
        type: "email",
      });
      if (error) {
        setErr(codeMessage(error.message));
      } else {
        navigate(destination, { replace: true });
      }
    } catch (e: any) {
      setErr(codeMessage(String(e?.message ?? e)));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="app">
      <div className="spacer" />
      <h1>How Y&apos;all Been</h1>
      <p className="muted">A private game about keeping up with your people.</p>

      {phase === "email" ? (
        <form
          className="grid"
          onSubmit={(e) => { e.preventDefault(); if (!busy && emailLooksReal) send(); }}
        >
          <p className="muted">No password needed. Put in your email and we&apos;ll send you a code.</p>
          <input
            className="input"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            enterKeyHint="go"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          {err && <p className="error">{err}</p>}
          <button className="btn primary" type="submit" disabled={busy || !emailLooksReal}>
            {busy ? "Sending…" : "SEND MY CODE"}
          </button>
        </form>
      ) : (
        <>
          <p className="muted">
            We just emailed a code to <b>{cleanEmail}</b>. Go check your email, then come back here and type in
            the numbers. You can leave this screen open while you look.
          </p>
          <form
            className="grid"
            onSubmit={(e) => { e.preventDefault(); if (!busy && code.trim().length >= 4) verify(); }}
          >
            <input
              ref={codeRef}
              className="input code-input"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              enterKeyHint="go"
              maxLength={10}
              placeholder="Your code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            />
            {err && <p className="error">{err}</p>}
            {note && <p className="muted">{note}</p>}
            <button className="btn primary" type="submit" disabled={busy || code.trim().length < 4}>
              {busy ? "Checking…" : "SIGN IN"}
            </button>
          </form>
          <div className="row auth-actions">
            <button className="link" disabled={busy} onClick={() => { setCode(""); setErr(""); setNote(""); setPhase("email"); }}>
              Use a different email
            </button>
            <button className="link" disabled={busy} onClick={() => send(true)}>
              Send it again
            </button>
          </div>
          <p className="muted">
            That email also has a button you can tap. On iPhone, <b>don&apos;t use it</b>. It opens Safari and
            signs you in there instead of here. Just type the numbers.
          </p>
        </>
      )}
      <div className="spacer" />
    </div>
  );
}

// Invite landing + accept.
export function Join() {
  const { token } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [preview, setPreview] = useState<any>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [joining, setJoining] = useState(false);
  const [err, setErr] = useState("");
  const [proceedAnyway, setProceedAnyway] = useState(false);
  const attempted = useRef(false);

  // Remember the invite so the join can finish even if the sign-in lands elsewhere.
  useEffect(() => {
    if (token) localStorage.setItem("pending_invite", token);
  }, [token]);

  // A server or connection problem is not the same thing as a bad invite. Both
  // used to render "This invite isn't active", which sends someone to bother
  // their commissioner for a new link when the link was fine all along.
  const loadPreview = useCallback(() => {
    setLoadFailed(false);
    setPreview(null);
    api(`/api/invite-preview`, { query: { token: token! } })
      .then(setPreview)
      .catch(() => setLoadFailed(true));
  }, [token]);

  useEffect(() => { loadPreview(); }, [loadPreview]);

  // Once signed in on a valid invite, join automatically — no second tap needed.
  useEffect(() => {
    if (user && token && preview && preview.valid && !attempted.current) {
      attempted.current = true;
      join();
    }
    if (preview && !preview.valid) localStorage.removeItem("pending_invite");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, preview, token]);

  async function join() {
    if (!user) {
      if (token) localStorage.setItem("pending_invite", token);
      navigate("/login", { state: { from: `/join/${token}` } });
      return;
    }
    setJoining(true);
    setErr("");
    try {
      const r = await api<{ leagueId: string; alreadyMember?: boolean }>(`/api/join`, { body: { token } });
      localStorage.removeItem("pending_invite");
      // Someone already in the league does not need the welcome tour again.
      navigate(r.alreadyMember ? `/league/${r.leagueId}` : `/league/${r.leagueId}/onboarding`, { replace: true });
    } catch (e: any) {
      setErr(e?.message ?? "We couldn't get you in just now. Try again.");
      setJoining(false);
    }
  }

  if (loadFailed) {
    return (
      <div className="app">
        <div className="spacer" />
        <h1>We couldn&apos;t load this invite.</h1>
        <p className="muted">
          That is usually the connection, not the link. Check your signal and try again.
        </p>
        <button className="btn primary" onClick={loadPreview}>Try again</button>
        <div className="spacer" />
      </div>
    );
  }
  if (!preview) return <div className="app"><div className="loading">Checking your invite…</div></div>;
  if (!preview.valid) {
    const expired = preview.reason === "expired";
    // A host who closed the door is a different situation from a link that ran
    // out or a link that was mistyped, and telling somebody to ask for a fresh
    // one when the crew is simply closed sends them to bother their host for
    // something that would not help.
    const closed = preview.reason === "closed";
    return (
      <div className="app">
        <div className="spacer" />
        <h1>
          {closed ? "This crew is closed right now."
            : expired ? "This invite expired."
            : "We don't recognize this link."}
        </h1>
        <p className="muted">
          {closed
            ? "Your host has stopped new people joining for now. Ask them to open it back up."
            : expired
            ? "Invite links are good for two weeks. Ask your host to send you a fresh one and you're in."
            : "Double check you copied the whole link from the text. If it still doesn't work, ask your host to send a new one."}
        </p>
        {/* Someone who already has an account should not be stranded here. */}
        <button className="btn ghost" onClick={() => navigate(user ? "/" : "/login")}>
          {user ? "Go to my crews" : "Sign in instead"}
        </button>
        <div className="spacer" />
      </div>
    );
  }

  const leaguePreview = (
    <div className="card">
      <h2>{preview.leagueName}</h2>
      {preview.commissionerFirstName && (
        <p className="muted">Commissioner: {preview.commissionerFirstName}</p>
      )}
      <div className="row" style={{ flexWrap: "wrap" }}>
        <span className="pill">{preview.seasonDuration}</span>
        <span className="pill">
          {preview.memberCount} {preview.memberCount === 1 ? "person" : "people"} in so far
        </span>
      </div>
      <p>{preview.description}</p>
    </div>
  );

  // iPhone in another browser: nothing below will work until they're in Safari.
  if (isIOSOtherBrowser() && !isStandalone() && !user && !proceedAnyway) {
    return (
      <div className="app">
        <div className="spacer" />
        <h1>{COPY.invite.headline}</h1>
        {leaguePreview}
        <div className="card live">
          <span className="tag">Open this in Safari</span>
          <p>
            You're in another browser. On iPhone, only <b>Safari</b> can set this up so your phone pings
            you when questions drop. That's an Apple rule, not ours.
          </p>
          <ol className="steps">
            <li>Copy the web address at the top of this screen.</li>
            <li>Open the <b>Safari</b> app.</li>
            <li>Paste the address in and go.</li>
          </ol>
        </div>
        <button className="btn ghost" onClick={() => setProceedAnyway(true)}>
          Keep going here anyway
        </button>
        <div className="spacer" />
      </div>
    );
  }

  // iPhone Safari: put it on the home screen BEFORE signing in. Signing in here
  // signs in Safari — the home screen app would still be signed out, and there's
  // no way to carry that session across.
  if (isIOS() && !isStandalone() && !user && !proceedAnyway) {
    return (
      <div className="app">
        <div className="spacer" />
        <h1>{COPY.invite.headline}</h1>
        {leaguePreview}
        <div className="card live">
          <span className="tag">Do this first (about 15 seconds)</span>
          <p>
            Put How Y&apos;all Been on your home screen <b>before</b> you sign in. If you sign in right here
            instead, the icon version will still be signed out and you'll have to do it twice.
          </p>
          <InstallSteps />
          <InstallMedia which="install" />
          <InstallWhy />
          <p className="muted">
            Then sign in from the icon using this same email address.
          </p>
          {/* The icon opens a different storage area from Safari, so the invite
              waiting here does not travel with it. Telling somebody to keep the
              link is cheaper than letting them find out by landing nowhere. */}
          <p className="muted">
            <b>Keep this link handy.</b> The app will ask you to paste it once you&apos;re signed
            in, so don&apos;t delete the message it came in.
          </p>
        </div>
        <button className="btn ghost" onClick={() => setProceedAnyway(true)}>
          Not on an iPhone? Keep going here
        </button>
        <div className="spacer" />
      </div>
    );
  }

  return (
    <div className="app">
      <div className="spacer" />
      <h1>{COPY.invite.headline}</h1>
      {leaguePreview}
      {err && <p className="error">{err}</p>}
      <button className="btn primary" disabled={joining} onClick={join}>
        {joining ? "Getting you in…" : COPY.invite.cta}
      </button>
      {/* Opening the app now comes here first when a link is waiting, so a
          person whose join keeps failing needs a way back to the crews they
          already have. Dropping the link is what lets them leave. */}
      {err && user && (
        <button
          className="btn ghost"
          onClick={() => {
            try { localStorage.removeItem("pending_invite"); } catch { /* private mode */ }
            navigate("/", { replace: true });
          }}
        >
          Skip this for now
        </button>
      )}
      <div className="spacer" />
    </div>
  );
}
